import { isEmpty, omit, isNull } from 'lodash';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { ISidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { ISavedFilter } from '../../../components/sidebar-filters/saved-filter/interface';
import { CloudVensFilterKeys, TCloudVensFilterKey } from './CloudVenSidebarFilters';

export const transformCloudVenSavedFilterToUser = (
    filter: ISavedFilter['filter_values'],
    context: ISidebarFilterOptionsContext
): Record<TCloudVensFilterKey, string> => {
    let result: Record<TCloudVensFilterKey, string> = {} as Record<TCloudVensFilterKey, string>;

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid displaying them as "unknown"
        if (isUndefined(value) || isNull(value)) continue;

        switch (key) {
            case CloudVensFilterKeys.program_id:
                result[key] = value
                    .map((val: string) => {
                        const option = context.programOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;

            case CloudVensFilterKeys.status:
                result[key] = value ? context.venStatusOptions.find(option => option.value === value)?.label || '' : '';
                break;

            case CloudVensFilterKeys.dispatch_type:
                result[key] = value ? context.cloudVenDispatchTypeOptions.find(option => option.value === value)?.label || '' : '';
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    // Remove empty properties from the result object
    const emptyProperties = getEmptyFilterProperties(result);
    if (emptyProperties.length > 0) {
        result = omit(result, emptyProperties) as Record<TCloudVensFilterKey, string>;
    }

    return result;
};

export const transformCloudVenSavedFilterValuesFromDto = (filter: ISavedFilter['filter_values']): Record<TCloudVensFilterKey, unknown> => {
    const result: Record<TCloudVensFilterKey, unknown> = {} as Record<TCloudVensFilterKey, unknown>;

    // Convert values from DTO format to interface format
    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value)) continue;

        switch (key) {
            case CloudVensFilterKeys.program_id:
                result[key] = value;
                break;

            case CloudVensFilterKeys.status:
                result[key] = value;
                break;

            case CloudVensFilterKeys.dispatch_type:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = value; // Keep other values as is
        }
    }

    return result;
};

export const transformCloudVensFilterValuesToDto = (filter: ISavedFilter['filter_values']): Record<TCloudVensFilterKey, unknown> => {
    const result: Record<TCloudVensFilterKey, unknown> = {} as Record<TCloudVensFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        switch (key) {
            case CloudVensFilterKeys.program_id:
                result[key] = value;
                break;

            case CloudVensFilterKeys.status:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    return result;
};

export const checkFilterInvalidValues = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    const invalidFields: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        if (isNil(value) || isEmpty(value)) continue;

        switch (key) {
            case CloudVensFilterKeys.program_id:
                if (Array.isArray(value)) {
                    const validOptionValues = context.programOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case CloudVensFilterKeys.status:
                if (typeof value === 'string') {
                    const validStatusValues = context.venStatusOptions.map(opt => opt.value);
                    if (!validStatusValues.includes(value)) {
                        invalidFields.push(key);
                    }
                }
                break;

            case CloudVensFilterKeys.dispatch_type:
                if (typeof value === 'string') {
                    const validDispatchTypeValues = context.cloudVenDispatchTypeOptions.map(opt => opt.value);
                    if (!validDispatchTypeValues.includes(value)) {
                        invalidFields.push(key);
                    }
                }
                break;

            default:
                // For other fields, you can add additional validation logic if needed
                break;
        }
    }

    return invalidFields;
};

export function countNotEmptyFilters(filters: Record<string, any>): number {
    if (!filters) return 0;

    return Object.values(filters).filter(value => {
        if (Array.isArray(value)) return value.length > 0;

        if (typeof value === 'number') return !isNaN(value);

        if (typeof value === 'string') return value.trim() !== '';

        if (typeof value === 'boolean') return true;

        return false;
    }).length;
}

function getEmptyFilterProperties(obj: Record<TCloudVensFilterKey, string>): TCloudVensFilterKey[] {
    return Object.entries(obj)
        .filter(([_, value]) => isEmpty(value))
        .map(([key, _]) => key) as TCloudVensFilterKey[];
}
